/**
 * Dates, in the only format this product may render them: dd/mm/yyyy.
 *
 * This file exists because of one specific bug. `2026-03-04` is the fourth of
 * March. Rendered through the browser's default locale it becomes `3/4/2026`
 * for a reader in the United States, which is the third of April -- a month of
 * coverage, appearing or disappearing, with nothing on screen to say anything
 * went wrong. An effective date read wrongly is a coverage error, not a
 * cosmetic one, so neither the browser's locale nor its timezone is allowed a
 * vote.
 *
 * There are two kinds of value and they are handled differently on purpose:
 *
 *   - A **calendar date** (`2026-03-04`) has no time and no timezone. It is
 *     split with a regular expression and reassembled. It never becomes a
 *     `Date`, because `new Date('2026-03-04')` is midnight *UTC*, which in
 *     Brazil (UTC-3) is 21:00 on the third -- so formatting it locally moves an
 *     effective date one day earlier. That is the classic version of this bug
 *     and it is structurally impossible here.
 *
 *   - An **instant** (`2026-03-04T12:30:00Z`) is a real moment. It is rendered
 *     in the product's timezone, stated explicitly, never the browser's.
 */

/** Every timestamp is shown in Brazilian time, wherever the reader happens to be. */
export const APP_TIME_ZONE = 'America/Sao_Paulo';
export const APP_LOCALE = 'pt-BR';

/** What to render when there is no value. Not an empty cell, which reads as a bug. */
export const EMPTY_VALUE = '—';

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const CYCLE_MONTH = /^(\d{4})-(\d{2})$/;

/**
 * The months in pt-BR, as a table rather than as anything `Intl` produces.
 *
 * A cycle month is `2026-04`: a label for a month, not a moment. Turning it
 * into a `Date` to get its name is the same bug this file exists to prevent --
 * `new Date('2026-04')` is midnight UTC, which in Brazil is the evening of the
 * 31st of March, so April would render as "março" for every reader in the
 * country the product is for.
 */
const MONTH_NAMES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

// Date and time are formatted separately and joined with a space. Asking Intl
// for both at once gets a locale-chosen separator -- today a comma, tomorrow
// whatever CLDR decides -- and the product's format is not negotiable.
const timeFormatter = new Intl.DateTimeFormat(APP_LOCALE, {
  timeZone: APP_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const instantAsDateFormatter = new Intl.DateTimeFormat(APP_LOCALE, {
  timeZone: APP_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function isValidDate(value: Date): boolean {
  return !Number.isNaN(value.getTime());
}

/**
 * A calendar date or an instant, as `dd/mm/yyyy`.
 *
 * A calendar date is rendered exactly as stored. An instant is rendered as the
 * day it falls on in {@link APP_TIME_ZONE}.
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }

  if (typeof value === 'string') {
    const parts = CALENDAR_DATE.exec(value);
    if (parts) {
      const [, year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
  }

  const instant = value instanceof Date ? value : new Date(value);
  return isValidDate(instant) ? instantAsDateFormatter.format(instant) : EMPTY_VALUE;
}

/** An instant, as `dd/mm/yyyy HH:mm` in {@link APP_TIME_ZONE}. */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }

  // A calendar date has no time of day to show; showing 00:00 would be an
  // invention, so it degrades to a date.
  if (typeof value === 'string' && CALENDAR_DATE.test(value)) {
    return formatDate(value);
  }

  const instant = value instanceof Date ? value : new Date(value);
  if (!isValidDate(instant)) {
    return EMPTY_VALUE;
  }
  return `${instantAsDateFormatter.format(instant)} ${timeFormatter.format(instant)}`;
}

/**
 * `dd/mm/yyyy` back to the `yyyy-mm-dd` the API speaks, or undefined if it is
 * not a real date. Rejects `31/02/2026` rather than rolling it into March.
 */
export function parseBrazilianDate(value: string): string | undefined {
  const parts = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!parts) {
    return undefined;
  }
  const [, day, month, year] = parts as unknown as [string, string, string, string];
  const asNumbers = { day: Number(day), month: Number(month), year: Number(year) };
  const probe = new Date(Date.UTC(asNumbers.year, asNumbers.month - 1, asNumbers.day));
  const roundTrips =
    probe.getUTCFullYear() === asNumbers.year &&
    probe.getUTCMonth() === asNumbers.month - 1 &&
    probe.getUTCDate() === asNumbers.day;
  return roundTrips ? `${year}-${month}-${day}` : undefined;
}

/**
 * A cycle month (`2026-04`) as `abril de 2026`.
 *
 * Lower case, because it is written into a sentence far more often than it
 * heads one. Anything that is not a cycle month comes back untouched, for the
 * same reason {@link formatCpf} returns what it was given: showing the raw
 * value is honest, and inventing a shape for it is not.
 */
export function formatCycleMonth(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }
  const parts = CYCLE_MONTH.exec(value);
  if (!parts) {
    return value;
  }
  const [, year, month] = parts as unknown as [string, string, string];
  const name = MONTH_NAMES[Number(month) - 1];
  return name === undefined ? value : `${name} de ${year}`;
}

/**
 * An instant as `yyyy-mm-ddThh:mm` in {@link APP_TIME_ZONE} -- what a
 * `<input type="datetime-local">` holds.
 *
 * The input's value is a *naive* datetime by specification, and the API reads a
 * naive datetime as São Paulo wall-clock time. So the conversion has to happen
 * in the product's timezone and not in the browser's: seeded from the browser's
 * instead, somebody in Lisbon would open the control, press apply without
 * touching it, and move the deployment's clock four hours.
 *
 * Built from `formatToParts` rather than from a locale pattern, because the
 * shape here is prescribed by HTML rather than chosen by a locale, and every
 * locale that renders `2026-08-20` also renders `20/08/2026` somewhere.
 */
export function formatDateTimeForInput(value: string | Date): string {
  const instant = value instanceof Date ? value : new Date(value);
  if (!isValidDate(instant)) {
    return '';
  }
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const field = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${field('year')}-${field('month')}-${field('day')}T${field('hour')}:${field('minute')}`;
}

/** Today, as the API's `yyyy-mm-dd`, in the product's timezone rather than the browser's. */
export function todayInAppTimeZone(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return parts;
}
